import React, { useState, useEffect, useCallback } from 'react';

const BACKEND = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin);
const MAX = 12;

const KINDS = [
  ['floor-plan', '📐 Floor plan'],
  ['reference', '🖼️ Example picture'],
  ['other', '📎 Other'],
];

/**
 * RoomAttachmentsModal — floor plans and example pictures held against a room.
 * These print full-page after that room's items on the load-in sheets, so a
 * crew on site has the plan in the same stack as the photos.
 */
export default function RoomAttachmentsModal({ projectId, roomId, roomName, onClose, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [kind, setKind] = useState('floor-plan');
  const [caption, setCaption] = useState('');
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/projects/${projectId}/rooms/${roomId}/attachments`);
      if (!res.ok) throw new Error(`Could not load attachments (${res.status})`);
      setItems(await res.json());
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [projectId, roomId]);

  useEffect(() => { load(); }, [load]);

  const upload = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      Array.from(files).forEach(f => fd.append('files', f));
      fd.append('kind', kind);
      fd.append('caption', caption);
      const res = await fetch(`${BACKEND}/api/projects/${projectId}/rooms/${roomId}/attachments`, {
        method: 'POST', body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `Upload failed (${res.status})`);
      setCaption('');
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND}/api/room-attachments/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const full = items.length >= MAX;

  return (
    <div className="fixed inset-0 bg-black/85 flex items-center justify-center p-4 z-[70]">
      <div className="bg-[#1E293B] border-2 border-[#D4A574] rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-bold text-[#D4A574]">📎 Attachments — {roomName}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="att-close">✕</button>
        </div>
        <p className="text-[11px] text-[#B49B7E] mb-4">
          Floor plans and example pictures for this room. Each one prints on its own page after this
          room&apos;s items on the load-in sheet. Up to {MAX} per room.
        </p>

        {error && <p className="text-red-400 text-sm mb-3" data-testid="att-error">{error}</p>}

        <div className="space-y-2 mb-4 border border-[#D4A574]/30 rounded p-3">
          <div className="flex gap-2 flex-wrap">
            {KINDS.map(([k, lbl]) => (
              <button key={k} onClick={() => setKind(k)} data-testid={`att-kind-${k}`}
                className={`px-2.5 py-1 rounded text-xs ${kind === k ? 'bg-[#D4A574] text-black font-bold' : 'bg-slate-700 text-gray-200'}`}>
                {lbl}
              </button>
            ))}
          </div>
          <input value={caption} onChange={e => setCaption(e.target.value)}
            placeholder="Caption (prints under the image) — optional"
            data-testid="att-caption"
            className="w-full bg-black/40 border border-[#D4A574]/30 rounded px-2 py-1 text-white text-sm" />
          <label className={`block text-center px-3 py-2 rounded text-sm font-medium ${full || busy ? 'bg-slate-800 text-gray-500 cursor-not-allowed' : 'bg-amber-700 hover:bg-amber-600 text-white cursor-pointer'}`}>
            {busy ? 'Uploading…' : full ? `Maximum of ${MAX} reached` : '⬆️ Choose image(s)'}
            <input type="file" accept="image/*" multiple hidden disabled={full || busy}
              data-testid="att-file"
              onChange={e => { upload(e.target.files); e.target.value = ''; }} />
          </label>
        </div>

        {loading ? (
          <p className="text-center text-[#D4C5A9] py-6 text-sm">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-center text-gray-500 py-6 text-sm italic" data-testid="att-empty">
            Nothing attached to this room yet.
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {items.map(a => (
              <div key={a.id} className="border border-[#D4A574]/30 rounded overflow-hidden" data-testid={`att-card-${a.id}`}>
                <button onClick={() => setViewing(a)} className="block w-full" title="View full size">
                  <img src={a.data_url} alt={a.name} className="w-full h-24 object-cover" />
                </button>
                <div className="p-1.5">
                  <p className="text-[10px] text-[#D4A574] uppercase tracking-wide">
                    {(KINDS.find(k => k[0] === a.kind) || ['', a.kind])[1]}
                  </p>
                  <p className="text-[11px] text-white truncate" title={a.caption || a.name}>{a.caption || a.name}</p>
                  <button onClick={() => remove(a.id)} disabled={busy}
                    data-testid={`att-remove-${a.id}`}
                    className="mt-1 text-[11px] text-red-400 hover:text-red-300 disabled:opacity-40">Remove</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {viewing && (
        <div className="fixed inset-0 bg-black/95 flex items-center justify-center p-6 z-[80]"
          onClick={() => setViewing(null)}>
          <img src={viewing.data_url} alt={viewing.name} className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </div>
  );
}
